import type {
  ICar,
  ICarsRecentSearches,
  ICondo,
  ICondoLocation,
  ICondoRecentSearches,
  IHotel,
  IWeek,
} from '@share/common-types';

export const RECENT_SEARCH_PREVIEW_STORAGE_MAX = 6;

export function getRecentSearchLocationKeyHotel(entry: {
  locationCode?: unknown;
  location?: { code?: unknown; name?: string };
}): string {
  const raw = entry?.locationCode ?? entry?.location?.code;
  if (raw !== undefined && raw !== null && String(raw) !== '') {
    return String(raw);
  }
  const name = entry?.location?.name;
  return name ? `name:${String(name).toLowerCase().trim()}` : '';
}

export function getRecentSearchLocationKeyCondo(entry: ICondoRecentSearches): string {
  const loc = entry?.location as ICondoLocation | undefined;
  const raw = loc?.id;
  if (raw !== undefined && raw !== null && String(raw) !== '') {
    return `condo:${String(raw)}`;
  }
  const name = loc?.name;
  return name ? `condo:name:${String(name).toLowerCase().trim()}` : '';
}

export function getRecentSearchLocationKeyCars(entry: ICarsRecentSearches): string {
  const pick = entry?.pickUpLocation?.code ?? entry?.pickUpLocation?.name;
  const drop = entry?.dropOffLocation?.code ?? entry?.dropOffLocation?.name;
  return `cars:${String(pick ?? '')}|${String(drop ?? '')}`;
}

export function getRecentSearchLocationKeyWeeks(entry: {
  propertyRequest?: { location?: ICondoLocation };
}): string {
  const loc = entry?.propertyRequest?.location;
  const raw = loc?.id;
  if (raw !== undefined && raw !== null && String(raw) !== '') {
    return `weeks:${String(raw)}`;
  }
  const name = loc?.name;
  return name ? `weeks:name:${String(name).toLowerCase().trim()}` : '';
}

export function getHotelRecentPreviewImages(hotels: IHotel[] | null | undefined): string[] {
  if (!hotels?.length) return [];
  return hotels
    .slice(0, RECENT_SEARCH_PREVIEW_STORAGE_MAX)
    .map((h) => h?.images?.[0]?.url)
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

export function getCarRecentPreviewData(cars: ICar[] | null | undefined): {
  images: string[];
  logos: (string | undefined)[];
} {
  if (!cars?.length) return { images: [], logos: [] };
  const pairs = cars
    .slice(0, RECENT_SEARCH_PREVIEW_STORAGE_MAX)
    .map((c) => {
      const logoUrl = c?.info?.tpA_Extensions?.supplierLogo?.url;
      return {
        image: c?.vehicle?.pictureURL,
        logo: typeof logoUrl === 'string' && logoUrl.length > 0 ? logoUrl : undefined,
      };
    })
    .filter((p): p is { image: string; logo: string | undefined } => typeof p.image === 'string' && p.image.length > 0);

  return {
    images: pairs.map((p) => p.image),
    logos: pairs.map((p) => p.logo),
  };
}

export function getCondoRecentPreviewImages(condos: ICondo[] | null | undefined): string[] {
  if (!condos?.length) return [];
  return condos
    .slice(0, RECENT_SEARCH_PREVIEW_STORAGE_MAX)
    .map((c) => c?.images?.[0]?.url)
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

export function getWeekRecentPreviewImages(weeks: IWeek[] | null | undefined): string[] {
  if (!weeks?.length) return [];
  return weeks
    .slice(0, RECENT_SEARCH_PREVIEW_STORAGE_MAX)
    .map((w) => w?.images?.[0]?.url)
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}
